import React from 'react';
import { Checkbox } from '../ui/checkbox';
import { Minus, Plus, AlertTriangle, Info } from 'lucide-react';
import { vehicleUplift } from '../../utils/quotationUtils';

const countOf = (v, counts) => counts[v.vehicle_type] ?? v.vehicle_count ?? 1;

// Seat total for the base at its chosen count; null when capacity is unknown.
const baseSeats = (v, counts) => (v?.max_pax_capacity ? v.max_pax_capacity * countOf(v, counts) : null);

const CountStepper = ({ value, onChange, testId }) => (
  <div className="inline-flex items-center border border-navy/20 rounded" data-testid={testId}>
    <button type="button" className="px-1.5 py-1 disabled:opacity-30 hover:bg-gray-100" disabled={value <= 1}
      onClick={() => onChange(value - 1)} data-testid={`${testId}-minus`} aria-label="Fewer vehicles">
      <Minus className="w-3 h-3" />
    </button>
    <span className="w-7 text-center font-medium" data-testid={`${testId}-value`}>{value}</span>
    <button type="button" className="px-1.5 py-1 hover:bg-gray-100" onClick={() => onChange(value + 1)}
      data-testid={`${testId}-plus`} aria-label="More vehicles">
      <Plus className="w-3 h-3" />
    </button>
  </div>
);

const UpliftCell = ({ isBase, uplift, formatCurrency }) => {
  if (isBase) return <span className="text-blue-600 font-medium">Included</span>;
  if (uplift > 0) return <span className="text-orange-600">+ {formatCurrency(uplift)}</span>;
  if (uplift < 0) return <span className="text-green-600">Save {formatCurrency(Math.abs(uplift))}</span>;
  return <span className="text-gray-400">-</span>;
};

export const CustomVehicleTable = ({
  vehicles, baseVehicleType, counts, selectedVehicles, seatsNeeded, routeName,
  formatCurrency, onBaseChange, onTick, onCountChange,
}) => {
  const base = vehicles.find((v) => v.vehicle_type === baseVehicleType) || vehicles[0];
  const seats = baseSeats(base, counts);
  const short = seats !== null && seats < seatsNeeded;
  return (
    <div className="space-y-2">
      {routeName && (
        <p className="text-xs text-gray-600 flex items-center gap-1" data-testid="custom-vehicle-route-note">
          <Info className="w-3.5 h-3.5" /> Route price used: {routeName}
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-navy/20" data-testid="custom-vehicle-table">
          <thead>
            <tr className="bg-gray-50 border-b border-navy/20">
              <th className="p-2 w-14 text-center text-navy font-semibold">Base</th>
              <th className="p-2 w-10 text-center" aria-label="Offer as upgrade"></th>
              <th className="p-2 text-left text-navy font-semibold">Vehicle Type</th>
              <th className="p-2 text-center text-navy font-semibold">No. of Vehicles</th>
              <th className="p-2 text-right text-navy font-semibold">Uplift</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map((v) => {
              const vt = v.vehicle_type;
              const isBase = vt === base?.vehicle_type;
              const ticked = !isBase && selectedVehicles.includes(vt);
              return (
                <tr key={vt} className={`border-b border-navy/10 ${isBase ? 'bg-blue-50' : ticked ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                  data-testid={`custom-vehicle-row-${vt}`}>
                  <td className="p-2 text-center">
                    <input type="radio" name="custom_pkg_base_vehicle" checked={isBase} onChange={() => onBaseChange(vt)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500" data-testid={`custom-vehicle-base-${vt}`} />
                  </td>
                  <td className="p-2 text-center">
                    <Checkbox checked={isBase || ticked} disabled={isBase} onCheckedChange={(c) => onTick(vt, !!c)}
                      className="h-4 w-4" data-testid={`custom-vehicle-tick-${vt}`} />
                  </td>
                  <td className="p-2 font-medium">
                    {v.vehicle_label || v.vehicle_type}
                    {v.max_pax_capacity ? <span className="text-gray-500 font-normal"> ({v.max_pax_capacity} seats)</span> : null}
                    {isBase && <span className="ml-2 text-blue-600 text-[10px] font-semibold">(BASE)</span>}
                  </td>
                  <td className="p-2 text-center">
                    <CountStepper value={countOf(v, counts)} onChange={(n) => onCountChange(vt, n)} testId={`custom-vehicle-count-${vt}`} />
                  </td>
                  <td className="p-2 text-right" data-testid={`custom-vehicle-uplift-${vt}`}>
                    <UpliftCell isBase={isBase} uplift={vehicleUplift(v, base)} formatCurrency={formatCurrency} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-gray-500">
        Base vehicle is included in the package price. Ticked vehicles are offered as upgrades: the guest pays the uplift on top of the package price.
        Counts are suggested from {seatsNeeded} seats (adults + children); press Recalculate after changing a count.
      </p>
      {short && (
        <p className="text-xs text-red-600 flex items-center gap-1" data-testid="custom-vehicle-seat-warning">
          <AlertTriangle className="w-3.5 h-3.5" /> Base vehicles seat {seats}, but the group needs {seatsNeeded} seats.
        </p>
      )}
    </div>
  );
};
